import Button from "components/common/Button";
import CustomToast from "components/common/CustomToast";
import ModalContainer, { ModalContainerProps } from "components/common/ModalContainer";
import { getFromStorage } from "helpers";
import { useLeaveSpace } from "hooks/spaceHooks";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Space } from "redux/Ldb/types/spaceTypes";
import * as UrlConstants from "constants/urls";

type SpaceDetailsSettingsLeaveModalProps = { spaceData: Space } & Omit<
    ModalContainerProps,
    "children"
>;

const SpaceDetailsSettingsLeaveModal = (props: SpaceDetailsSettingsLeaveModalProps) => {
    const { spaceData, closeModal, ...rest } = props;
    const navigate = useNavigate();
    const [leaveSpaceMutation, leaveSpaceMutationResult] = useLeaveSpace();
    const userProfile = getFromStorage("ally-user");
    const isGuest = userProfile?.isGuest;

    async function handleLeaveSpace() {
        try {
            await leaveSpaceMutation(spaceData?._id);
            toast.custom((t) => (
                <CustomToast t={t} message="Space left successfully" type="success" />
            ));
            if (isGuest) {
                navigate(UrlConstants.SPACES_GUEST);
            } else {
                navigate(UrlConstants.SPACES);
            }
        } catch (error) {
            toast.custom((t) => <CustomToast t={t} message={error?.message} type="error" />);
        }
    }

    return (
        <ModalContainer
            showCloseIcon={false}
            tailwindClassName="w-full max-w-[500px] max-h-[90vh] bg-white rounded-lg p-[16px]"
            closeModal={() => {
                closeModal?.();
            }}
            {...rest}
        >
            <div>
                <div className="flex justify-between items-center">
                    <p className="text-lg font-rocGroteskMedium text-g-75">Leave space </p>
                    <i
                        className="ri-close-fill text-xl text-gm-50 cursor-pointer"
                        onClick={() => {
                            closeModal?.();
                        }}
                    ></i>
                </div>

                <div className="pt-1 max-h-[66vh] overflow-y-auto">
                    <p className="text-sm font-rocGroteskMedium text-slate-500">
                        Are you sure you want to leave this space? Once you leave, you’ll lose
                        access to its content unless you’re invited back.
                    </p>

                    <div className="flex items-center justify-end gap-2 mt-[24px]">
                        <Button
                            btnText="No, cancel"
                            onClick={() => {
                                closeModal?.();
                            }}
                            btnClassname="!w-fit whitespace-nowrap !py-[8px] !px-[12px] !bg-slate-100 !text-g-75"
                            disabled={leaveSpaceMutationResult.isLoading}
                        />
                        <Button
                            btnText="Leave space"
                            onClick={handleLeaveSpace}
                            isLoading={leaveSpaceMutationResult.isLoading}
                            btnClassname="!w-fit whitespace-nowrap !py-[8px] !px-[12px] !bg-[#E4281D] !text-white"
                        />
                    </div>
                </div>
            </div>
        </ModalContainer>
    );
};

export default SpaceDetailsSettingsLeaveModal;
